import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

export interface RecentSearch {
  id: string;
  name: string;
  type: 'vessel' | 'port';
  metadata: {
    // Port fields
    locode?: string;
    country?: string;
    coordinates?: [number, number];
    // Vessel fields - match the types from ShipSearchResult/AutoCompleteShipInfo
    mmsi?: string | number | null;
    imo?: string | number | null;
    callSign?: string | null;
    shipType?: string | null;
    requestType?: 'ais' | 'no-recent-ais' | 'database' | 'ship-db';
  };
  timestamp: number;
}

interface RecentSearchesContextProps {
  recentSearches: RecentSearch[];
  addRecentSearch: (search: Omit<RecentSearch, 'timestamp'>) => void;
  clearRecentSearches: () => void;
}

const RecentSearchesContext = createContext<
  RecentSearchesContextProps | undefined
>(undefined);

const MAX_RECENT_SEARCHES = 10;

interface RecentSearchesProviderProps {
  children: ReactNode;
}

export const RecentSearchesProvider = ({
  children,
}: RecentSearchesProviderProps) => {
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>([]);

  const addRecentSearch = useCallback(
    (search: Omit<RecentSearch, 'timestamp'>) => {
      setRecentSearches((prev) => {
        // Remove existing entry with same id and type
        const filtered = prev.filter(
          (s) => !(s.id === search.id && s.type === search.type)
        );

        // Add new search at the beginning with timestamp
        const newSearch: RecentSearch = {
          ...search,
          timestamp: Date.now(),
        };

        // Keep only the most recent searches
        return [newSearch, ...filtered].slice(0, MAX_RECENT_SEARCHES);
      });
    },
    []
  );

  const clearRecentSearches = useCallback(() => {
    setRecentSearches([]);
  }, []);

  const value = useMemo(
    () => ({
      recentSearches,
      addRecentSearch,
      clearRecentSearches,
    }),
    [recentSearches, addRecentSearch, clearRecentSearches]
  );

  return (
    <RecentSearchesContext.Provider value={value}>
      {children}
    </RecentSearchesContext.Provider>
  );
};

export const useRecentSearches = () => {
  const context = useContext(RecentSearchesContext);
  if (!context) {
    throw new Error(
      'useRecentSearches must be used within a RecentSearchesProvider'
    );
  }
  return context;
};
